import axios from 'axios';
import shortid from 'shortid';
import utf8 from 'utf8';
import { toast } from 'react-toastify';

import { OhsLinkAccessAttachmentsUploadHeader } from 'global-services/constants/OhsStorageNames';
import OhsApiRequest from 'global-services/api/OhsApiRequest';
import { ModuleType } from 'global-services/constants/OhsObject';
import { OhsApiRequestName, RequestType } from 'global-services/api/OhsApiModels';
import { getCurrentTimezone } from 'global-services/OhsDataParse';

import { OhsAttachment, OhsFileState } from './OhsAttachmentModels';

export function fileNameTrim(input: string | undefined) {
  if (!input) return input;
  let trimedLongFileName = '';
  const inputArray = input.split('.');
  const fileType = inputArray[inputArray.length - 1];
  if (input.length > 50) {
    trimedLongFileName = `${input.substr(0, 30)}(...).${fileType}`;
  } else {
    trimedLongFileName = input;
  }
  return trimedLongFileName;
}

export function fileSizeParse(size: number) {
  if (size === undefined || size === null) return 'unknown';
  const i = Math.floor(Math.log(size) / Math.log(1024));
  return `${(size / 1024 ** i).toFixed(2) ?? 0 * 1} ${['B', 'kB', 'MB', 'GB', 'TB'][i]}`;
}

export function openOfficeFile(url: string | undefined | null) {
  if (url) {
    let openUrl = 'https://view.officeapps.live.com/op/view.aspx?src=';
    openUrl += encodeURIComponent(url);
    window.open(openUrl, '_blank');
  }
}

export function openOnGoogleDoc(url: string | undefined | null) {
  if (url) {
    window.open(`https://docs.google.com/gview?url=${encodeURIComponent(url)}`, '_blank');
  }
  // if (url) {
  //   let openUrl = 'https://docs.google.com/gview?url=';
  //   openUrl += encodeURIComponent(url);
  //   window.open(openUrl, '_blank');
  // }
}

export const downloadFileFromURL = (url: string, target = '_self'): void => {
  const a: HTMLAnchorElement = document.createElement('a');
  a.href = url;
  a.download = url.substr(url.lastIndexOf('/') + 1);
  a.target = target;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const openFileOnBrowser = async (attachment: OhsAttachment): Promise<void> => {
  const res: { items: { _ids: any[] } } | null = await OhsApiRequest(
    RequestType.Attachments,
    ModuleType.Attachment,
    OhsApiRequestName.List,
    { _ids: [attachment._id] }
  );

  const url = (res?.items?._ids || []).filter((a) => a)?.[0].url;
  const filePath = (res?.items?._ids || []).filter((a) => a)?.[0]?.details?.fileName?.toLowerCase();

  if (
    filePath.indexOf('.xlsx') >= 0 ||
    filePath.indexOf('.xlsb') >= 0 ||
    filePath.indexOf('.xls') >= 0 ||
    filePath.indexOf('.xlsm') >= 0
  ) {
    downloadFileFromURL(url);
  } else if (
    filePath.indexOf('.pptx') >= 0 ||
    filePath.indexOf('.ppsx') >= 0 ||
    filePath.indexOf('.ppt') >= 0 ||
    filePath.indexOf('.pps') >= 0 ||
    filePath.indexOf('.potx') >= 0 ||
    filePath.indexOf('.ppsm') >= 0
  ) {
    downloadFileFromURL(url);
  } else if (
    filePath.indexOf('.doc') >= 0 ||
    filePath.indexOf('.docx') >= 0 ||
    filePath.indexOf('.dotx') >= 0
  ) {
    openOfficeFile(url);
  } else if (filePath.indexOf('.pdf') >= 0) {
    downloadFileFromURL(url);
  } else {
    downloadFileFromURL(url);
  }
};

export const formatBase64String = (string: string) => {
  let decodedString: any;

  const padBase64String = (str: any) => {
    return str.padEnd(str.length + ((4 - (str.length % 4)) % 4), '=');
  };
  try {
    // Pad the Base64 string
    const paddedBase64String = padBase64String(string);

    // Decode the Base64 string
    decodedString = atob(paddedBase64String);
    // convert to utf8
    decodedString = utf8.decode(decodedString);
  } catch (error) {
    console.error('Error decoding or parsing Base64 string:', error);
  }
  return decodedString;
};

export const chunkSize = 5 * 1024 * 1024; // 5MB
export const MAX_ALLOWED_S3_FILE_SIZE = chunkSize * 10000;

const handleSinglePartUpload = async (
  file: File,
  id: string,
  setUploadState: (state: any) => void,
  linkAccessUploadToken?: string
) => {
  const signedUrlRes: Partial<OhsAttachment> | null = await OhsApiRequest(
    RequestType.Attachments,
    ModuleType.Attachment,
    linkAccessUploadToken ? OhsApiRequestName.LinkAccessUploadUrl : OhsApiRequestName.UploadUrl,
    { fileName: file.name },
    linkAccessUploadToken ? { linkAccessAttachmentsUploadHeader: linkAccessUploadToken } : undefined
  );
  if (!signedUrlRes?.key || !signedUrlRes?.url || !signedUrlRes?.bucket) return;

  const signedUrl = signedUrlRes.url;
  const uploadAxios = axios.create();

  await uploadAxios.put(signedUrl, file, {
    headers: { 'Content-Type': 'application/octet-stream' },
    onUploadProgress: (progressEvent) => {
      const progress = Math.round((progressEvent.loaded * 100) / file.size);
      setUploadState({ id, progress });
    },
  });

  const singlePartCompleteRes: any = await OhsApiRequest(
    RequestType.Attachments,
    ModuleType.Attachment,
    linkAccessUploadToken
      ? OhsApiRequestName.LinkAccessUploadSinglepartComplete
      : OhsApiRequestName.UploadSinglepartComplete,
    {
      key: signedUrlRes.key,
      tzDateUploaded: getCurrentTimezone(),
    },
    linkAccessUploadToken ? { linkAccessAttachmentsUploadHeader: linkAccessUploadToken } : undefined
  );
  if (singlePartCompleteRes?.key && singlePartCompleteRes?.bucket) {
    setUploadState({
      id,
      progress: 100,
      state: OhsFileState.Completed,
      details: JSON.stringify({
        ...singlePartCompleteRes,
        fileName: file.name,
      }),
    });
  }
};

const handleMultipartUpload = async (
  file: File,
  id: string,
  setUploadState: (state: any) => void,
  linkAccessUploadToken?: string
) => {
  const initMultipartRes: any = await OhsApiRequest(
    RequestType.Attachments,
    ModuleType.Attachment,
    linkAccessUploadToken
      ? OhsApiRequestName.LinkAccessUploadMultipartInitiate
      : OhsApiRequestName.UploadMultipartInitiate,
    { fileName: file.name },
    linkAccessUploadToken ? { linkAccessAttachmentsUploadHeader: linkAccessUploadToken } : undefined
  );

  const { key, uploadId, bucket } = initMultipartRes || {};
  if (!key || !uploadId || !bucket) return;

  let partsCompleted = 0;
  let paused = false;
  const totalParts = Math.ceil(file.size / chunkSize);
  const uploadedParts: Record<number, { etag: string; partNumber: number }> = {};
  const partControllers: Record<number, AbortController> = {};

  const uploadAxios = axios.create();

  const uploadPart = async (part: number) => {
    const start = (part - 1) * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    const chunk = file.slice(start, end);
    const controller = new AbortController();
    partControllers[part] = controller;

    const signedUrlRes: any = await OhsApiRequest(
      RequestType.Attachments,
      ModuleType.Attachment,
      linkAccessUploadToken
        ? OhsApiRequestName.LinkAccessUploadMultipartUrl
        : OhsApiRequestName.UploadMultipartUrl,
      { key, partNumber: part, uploadId },
      linkAccessUploadToken
        ? { linkAccessAttachmentsUploadHeader: linkAccessUploadToken }
        : undefined
    );
    if (!signedUrlRes?.url) return;

    let headers: any = { 'Content-Type': 'application/octet-stream' };
    if (linkAccessUploadToken) {
      headers = { ...headers, [OhsLinkAccessAttachmentsUploadHeader]: linkAccessUploadToken };
    }

    const uploadChunkRes = await uploadAxios.put(signedUrlRes.url, chunk, {
      signal: controller.signal,
      headers,
    });

    uploadedParts[part] = {
      etag: uploadChunkRes?.headers?.etag?.replace(/"/g, '') || '',
      partNumber: part,
    };

    partsCompleted += 1;
    setUploadState({ id, progress: Math.round((partsCompleted / totalParts) * 100) });
    delete partControllers[part];
  };

  const triggerUploads = async () => {
    const pendingParts =
      Array.from({ length: totalParts }, (_, i) => i + 1)?.filter((p) => !uploadedParts[p]) || [];

    for (let i = 0; i < pendingParts.length; i += 1) {
      if (paused) break;
      try {
        // eslint-disable-next-line no-await-in-loop
        await uploadPart(pendingParts[i]);
      } catch (err) {
        break;
      }
    }

    const allParts = Object.values(uploadedParts) || [];
    if (allParts.length === totalParts) {
      const multiPartCompleteRes: any = await OhsApiRequest(
        RequestType.Attachments,
        ModuleType.Attachment,
        linkAccessUploadToken
          ? OhsApiRequestName.LinkAccessUploadMultipartComplete
          : OhsApiRequestName.UploadMultipartComplete,
        { key, uploadId, parts: allParts, tzDateUploaded: getCurrentTimezone() },
        linkAccessUploadToken
          ? { linkAccessAttachmentsUploadHeader: linkAccessUploadToken }
          : undefined
      );

      if (multiPartCompleteRes?.key && multiPartCompleteRes?.bucket) {
        setUploadState({
          id,
          state: OhsFileState.Completed,
          progress: 100,
          details: JSON.stringify({
            ...multiPartCompleteRes,
            fileName: file.name,
          }),
        });
      }
    }
  };

  const uploadControl = {
    pause: () => {
      paused = true;
      Object.values(partControllers)?.forEach((c) => c?.abort());
      setUploadState({ id, state: OhsFileState.Paused });
    },
    resume: () => {
      if (!paused) return;
      paused = false;
      setUploadState({ id, state: OhsFileState.InProgress });
      triggerUploads();
    },
  };

  setUploadState({ id, upload: uploadControl });
  await triggerUploads();
};

export const uploadPresignedAttachment = async (
  file: File,
  setFiles: (state: any) => void,
  linkAccessUploadToken?: string
) => {
  if (file.size > MAX_ALLOWED_S3_FILE_SIZE) {
    toast.error('File too large for upload. Try uploading a smaller file.');
    return;
  }
  const id = shortid.generate();

  const isSmallFile = file.size < chunkSize;

  const setUploadState = (fileObject: any) => {
    setFiles((state: any) =>
      state.map((item: any) => {
        if (item?.id === fileObject?.id) {
          return { ...item, ...fileObject };
        }
        return item;
      })
    );
  };

  setFiles((state: OhsAttachment[]) => [
    ...state,
    {
      id,
      name: file.name,
      progress: 0,
      state: OhsFileState.InProgress,
      isMultipart: !isSmallFile,
      details: '',
    },
  ]);

  try {
    if (isSmallFile) {
      await handleSinglePartUpload(file, id, setUploadState, linkAccessUploadToken);
    } else {
      await handleMultipartUpload(file, id, setUploadState, linkAccessUploadToken);
    }
  } catch (err) {
    console.error('Upload failed:', err);
  }
};

export const getAttachmentList = async (payload: string[]): Promise<OhsAttachment[]> => {
  if (payload == null || !payload.length) return [];
  const res: { items: { _ids: any[] } } | null = await OhsApiRequest(
    RequestType.Attachments,
    ModuleType.Attachment,
    OhsApiRequestName.List,
    { _ids: payload }
  );
  let attachmentList: any[] = [];
  if (res) {
    attachmentList = (res.items?._ids || [])
      .filter((a) => a) // check if any attachment null or undefined
      .map((attachment) => ({
        ...attachment?.details,
        _id: attachment._id,
        url: attachment.url,
        type: attachment.type,
      }));
  }
  return attachmentList;
};

export const downloadAttachment = async (attachment: OhsAttachment): Promise<void> => {
  const res: any[] | null = await getAttachmentList([attachment._id || '']);

  const url = res[0]?.url ?? '';
  downloadFileFromURL(url);
};
